import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';

function NetworkVisualization() {
  const nodes = [
    { x: 50,  y: 50,  size: 12, delay: 0   },
    { x: 150, y: 80,  size: 16, delay: 0.3 },
    { x: 250, y: 60,  size: 14, delay: 0.6 },
    { x: 350, y: 90,  size: 18, delay: 0.9 },
    { x: 450, y: 70,  size: 12, delay: 1.2 },
    { x: 100, y: 150, size: 14, delay: 0.4 },
    { x: 200, y: 180, size: 20, delay: 0.7 },
    { x: 300, y: 160, size: 24, delay: 1.0 },
    { x: 400, y: 190, size: 16, delay: 1.3 },
    { x: 500, y: 170, size: 14, delay: 1.6 },
    { x: 80,  y: 250, size: 16, delay: 0.5 },
    { x: 180, y: 280, size: 18, delay: 0.8 },
    { x: 280, y: 260, size: 22, delay: 1.1 },
    { x: 380, y: 290, size: 20, delay: 1.4 },
    { x: 480, y: 270, size: 14, delay: 1.7 },
    { x: 120, y: 350, size: 14, delay: 0.6 },
    { x: 220, y: 380, size: 16, delay: 0.9 },
    { x: 320, y: 360, size: 18, delay: 1.2 },
    { x: 420, y: 390, size: 16, delay: 1.5 },
    { x: 520, y: 370, size: 12, delay: 1.8 },
    { x: 90,  y: 450, size: 12, delay: 0.7 },
    { x: 190, y: 480, size: 14, delay: 1.0 },
    { x: 290, y: 460, size: 16, delay: 1.3 },
    { x: 390, y: 490, size: 14, delay: 1.6 },
    { x: 490, y: 470, size: 12, delay: 1.9 },
    { x: 140, y: 550, size: 14, delay: 0.8 },
    { x: 240, y: 580, size: 16, delay: 1.1 },
    { x: 340, y: 560, size: 18, delay: 1.4 },
    { x: 440, y: 590, size: 14, delay: 1.7 },
    { x: 540, y: 570, size: 12, delay: 2.0 },
  ];

  const connections = [
    [0,1],[1,2],[2,3],[3,4],
    [5,6],[6,7],[7,8],[8,9],
    [10,11],[11,12],[12,13],[13,14],
    [15,16],[16,17],[17,18],[18,19],
    [20,21],[21,22],[22,23],[23,24],
    [25,26],[26,27],[27,28],[28,29],
    [0,5],[1,6],[2,7],[3,8],[4,9],
    [5,10],[6,11],[7,12],[8,13],[9,14],
    [10,15],[11,16],[12,17],[13,18],[14,19],
    [15,20],[16,21],[17,22],[18,23],[19,24],
    [20,25],[21,26],[22,27],[23,28],[24,29],
  ];

  return (
    <svg width="580" height="600" className="opacity-80">
      {connections.map((conn, idx) => (
        <line
          key={idx}
          x1={nodes[conn[0]].x}
          y1={nodes[conn[0]].y}
          x2={nodes[conn[1]].x}
          y2={nodes[conn[1]].y}
          stroke="#28D7C6"
          strokeWidth="1"
          opacity="0.35"
        />
      ))}
      {nodes.map((node, idx) => (
        <g key={idx}>
          <circle
            cx={node.x}
            cy={node.y}
            r={node.size}
            fill="#28D7C6"
            opacity="0.75"
            className="animate-pulse"
            style={{ animationDelay: `${node.delay}s`, animationDuration: '3s' }}
          />
          {node.size > 18 && (
            <circle
              cx={node.x}
              cy={node.y}
              r={node.size + 8}
              fill="none"
              stroke="#28D7C6"
              strokeWidth="1"
              opacity="0.25"
              className="animate-ping"
              style={{ animationDelay: `${node.delay}s`, animationDuration: '3s' }}
            />
          )}
        </g>
      ))}
    </svg>
  );
}

export default function Hero() {
  return (
    <section className="relative min-h-screen overflow-hidden bg-white dark:bg-[#0a1520] transition-colors duration-300">
      <Navbar />

      {/* Subtle dot grid background */}
      <div
        className="absolute inset-0 opacity-[0.03] dark:opacity-[0.06]"
        style={{
          backgroundImage: `radial-gradient(circle at center, #0E1A2B 1px, transparent 1px)`,
          backgroundSize: '48px 48px',
        }}
      />

      {/* Hero Content - Two Column */}
      <div className="relative z-10 max-w-[1280px] mx-auto px-12 pt-48 pb-32 flex items-center gap-16">

        {/* Left: Text */}
        <div className="flex-1 min-w-0">
          <h1 className="text-[72px] font-extralight leading-[1.05] tracking-[-0.02em] text-[#0E1A2B] dark:text-[#e8edf4] mb-7">
            Engineered Intelligence for Complex Operations
          </h1>

          <p className="text-[20px] font-light leading-[1.6] tracking-[-0.01em] text-[#5C6675] dark:text-[#8899aa] max-w-[520px] mb-12">
            We design and implement structured AI systems that help regulated and service-based businesses reduce operational friction, increase client conversion, and operate with measurable clarity.
          </p>

          <div className="flex items-center gap-5">
            <Link
              to="/solutions#audit"
              className="bg-[#0E1A2B] dark:bg-[#28D7C6] text-white dark:text-[#0E1A2B] h-[52px] px-8 rounded-full text-[16px] font-normal hover:bg-[#1a2d47] dark:hover:bg-[#20c4b5] transition-all whitespace-nowrap cursor-pointer inline-flex items-center"
            >
              Schedule an AI Strategy Session
            </Link>
            <Link
              to="/solutions"
              className="text-[#0E1A2B] dark:text-[#e8edf4] h-[52px] px-8 rounded-full text-[16px] font-normal border border-[#D9DEE5] dark:border-[#1e3247] hover:border-[#0E1A2B] dark:hover:border-[#28D7C6] transition-all whitespace-nowrap cursor-pointer inline-flex items-center"
            >
              Explore Solutions
            </Link>
          </div>
        </div>

        {/* Right: Network Visualization */}
        <div className="flex-shrink-0 flex items-center justify-center w-[580px] h-[600px]">
          <NetworkVisualization />
        </div>
      </div>
    </section>
  );
}
